import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, Button, EmptyState, Tabs, Text } from "@teals/ui-web";
import type { TabItem } from "@teals/ui-web";
import {
  useDeleteDataSourceMutation,
  useGetDataSourceQuery,
} from "../../store/api/dataSourcesApi";
import DataSourceDetail, {
  type DataSourceDetailTab,
} from "../DataSources/DataSourceDetail";
import {
  DS_TYPE_BADGE_VARIANT,
  DS_TYPE_LABELS,
  BYO_PROVIDER_LABELS,
} from "../DataSources/constants";
import { byoIndexName } from "../DataSources/connectionConfig";
import { formatRelativeTime } from "../DataSources/relativeTime";
import { PageWrapper } from "../DataSources/styles";
import {
  ConnectionStrip,
  Crumb,
  InstanceHeader,
  InstanceActions,
  InstanceTitleRow,
  StripItem,
} from "./styles";
import { INTEGRATIONS_HUB } from "./paths";

const DataSourceInstance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { data: ds, isLoading, error } = useGetDataSourceQuery(id ?? "", {
    skip: !id,
  });
  const [deleteDs] = useDeleteDataSourceMutation();

  const isByo = ds?.dsType === "byo";
  const activeTab: DataSourceDetailTab =
    tabParam === "upload" && !isByo ? "upload" : "overview";

  const tabItems: TabItem[] = useMemo(() => {
    const items: TabItem[] = [{ id: "overview", label: "Overview" }];
    if (!isByo) items.push({ id: "upload", label: "Upload Data" });
    return items;
  }, [isByo]);

  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === "overview") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const handleDelete = async () => {
    if (!ds) return;
    try {
      await deleteDs(ds.dataSourceId).unwrap();
      navigate(INTEGRATIONS_HUB);
    } catch {
      /* RTK error state handles display */
    }
  };

  if (!id) {
    return (
      <PageWrapper>
        <EmptyState title="Missing data source id." />
      </PageWrapper>
    );
  }

  if (isLoading) {
    return (
      <PageWrapper>
        <EmptyState title="Loading data source…" />
      </PageWrapper>
    );
  }

  if (error || !ds) {
    return (
      <PageWrapper>
        <Crumb>
          <Link to={INTEGRATIONS_HUB}>Integrations</Link>
          {" → Data source"}
        </Crumb>
        <EmptyState
          title="Data source not found."
          action={
            <Button variant="outline" onClick={() => navigate(INTEGRATIONS_HUB)}>
              Back to Integrations
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  const recordLabel =
    ds.recordCount != null && ds.recordCount > 0
      ? ds.recordCount.toLocaleString()
      : "0";
  const indexName = isByo ? byoIndexName(ds.connectionConfig) : undefined;
  const providerLabel = ds.provider
    ? (BYO_PROVIDER_LABELS[ds.provider] ?? ds.provider)
    : null;

  return (
    <PageWrapper>
      <Crumb>
        <Link to={INTEGRATIONS_HUB}>Integrations</Link>
        {` → ${ds.name}`}
      </Crumb>

      <InstanceHeader>
        <div>
          <InstanceTitleRow>
            <Text variant="h2">{ds.name}</Text>
            <Badge variant={DS_TYPE_BADGE_VARIANT[ds.dsType] as any} size="sm">
              {DS_TYPE_LABELS[ds.dsType]}
            </Badge>
            <Badge
              variant={ds.status === "active" ? "success" : "warning"}
              size="sm"
            >
              {ds.status}
            </Badge>
          </InstanceTitleRow>
          <Text
            variant="body1"
            style={{
              color: "var(--text-muted)",
              fontSize: "0.875rem",
              maxWidth: "36rem",
              marginTop: "0.375rem",
            }}
          >
            Retrieval context for associated agents. Schema and query filters
            apply on every search.
          </Text>
        </div>
        <InstanceActions>
          {deleteConfirm ? (
            <>
              <Text
                variant="body1"
                style={{
                  color: "var(--danger, #dc2626)",
                  alignSelf: "center",
                }}
              >
                Are you sure?
              </Text>
              <Button variant="ghost" onClick={() => setDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleDelete}
                style={{ background: "var(--danger, #dc2626)" }}
              >
                Delete
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              onClick={() => setDeleteConfirm(true)}
              style={{ color: "var(--danger, #dc2626)" }}
            >
              Delete Data Source
            </Button>
          )}
        </InstanceActions>
      </InstanceHeader>

      <ConnectionStrip>
        <StripItem>
          <label>Data Source ID</label>
          <span style={{ fontFamily: "monospace" }}>{ds.dataSourceId}</span>
        </StripItem>
        <StripItem>
          <label>Records</label>
          <span>{recordLabel}</span>
        </StripItem>
        <StripItem>
          <label>Last synced</label>
          <span>{formatRelativeTime(ds.updatedAt)}</span>
        </StripItem>
        <StripItem>
          <label>Organisation</label>
          <span>{ds.orgName || "—"}</span>
        </StripItem>
        {isByo && (providerLabel || indexName) && (
          <StripItem>
            <label>{providerLabel || "Index"}</label>
            <span style={{ fontFamily: "monospace" }}>
              {indexName || "—"}
            </span>
          </StripItem>
        )}
      </ConnectionStrip>

      <div style={{ marginBottom: "1.25rem" }}>
        <Tabs items={tabItems} activeTab={activeTab} onChange={setTab} />
      </div>

      <DataSourceDetail ds={ds} activeTab={activeTab} />
    </PageWrapper>
  );
};

export default DataSourceInstance;
